import React from 'react';
import { createRoot } from 'react-dom/client';
import HectorWidget from './components/HectorWidget';
import { WidgetConfig, ContactFormData } from './types';

// Interface pour l'API globale
interface IHectorWidgetAPI {
  init: (config?: WidgetConfig) => void;
  open: () => void;
  close: () => void;
  destroy: () => void;
  isOpen: () => boolean;
  version: string;
}

// État global du widget
let widgetContainer: HTMLElement | null = null;
let widgetRoot: any = null;
let currentConfig: WidgetConfig = { position: 'bottom' };
let isWidgetOpen = false;

// Fonction pour créer le conteneur du widget
const createWidgetContainer = (): HTMLElement => {
  // Supprimer l'ancien conteneur s'il existe
  const existingContainer = document.getElementById('hector-widget-container');
  if (existingContainer) {
    existingContainer.remove();
  }

  // Créer le nouveau conteneur
  const container = document.createElement('div');
  container.id = 'hector-widget-container';
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '0';
  container.style.width = '100%';
  container.style.height = '100%';
  container.style.pointerEvents = 'none';
  container.style.zIndex = '999999';
  
  document.body.appendChild(container);
  return container;
};

// Fonction pour initialiser le widget
const initWidget = (config?: WidgetConfig) => {
  console.log('🚀 Initialisation du Widget Hector...', config);
  
  // Mettre à jour la configuration
  currentConfig = { ...currentConfig, ...config };
  
  // Créer le conteneur
  widgetContainer = createWidgetContainer();
  
  // Créer la racine React
  widgetRoot = createRoot(widgetContainer);
  
  // Rendre le widget avec toutes les fonctionnalités intégrées
  widgetRoot.render(
    React.createElement(HectorWidget, {
      config: currentConfig,
      onSubmit: (data: ContactFormData) => {
        console.log('📝 Données du formulaire reçues:', data);
        
        // Appeler le callback personnalisé s'il existe
        if (currentConfig.onSubmit) {
          currentConfig.onSubmit(data);
        }
        
        // Analytics par défaut
        if (typeof (window as any).gtag !== 'undefined') {
          (window as any).gtag('event', 'hector_widget_submit', {
            'event_category': 'engagement',
            'event_label': 'form_submission'
          });
        }
      }
    })
  );
  
  isWidgetOpen = false;
  console.log('✅ Widget Hector initialisé avec succès - Toutes les fonctionnalités sont intégrées !');
};

// Fonction pour ouvrir le widget
const openWidget = () => {
  if (!widgetContainer || !widgetRoot) {
    console.warn('⚠️ Widget non initialisé. Appelez HectorWidget.init() d\'abord.');
    return;
  }
  
  // Simuler un clic sur le bouton du widget
  const widgetButton = document.querySelector('[data-hector-widget-button]');
  if (widgetButton) {
    (widgetButton as HTMLElement).click();
    isWidgetOpen = true;
    console.log('📱 Widget Hector ouvert');
  }
};

// Fonction pour fermer le widget
const closeWidget = () => {
  if (!widgetContainer || !widgetRoot) {
    console.warn('⚠️ Widget non initialisé.');
    return;
  }
  
  // Simuler un clic sur le bouton de fermeture
  const closeButton = document.querySelector('[data-hector-widget-close]');
  if (closeButton) {
    (closeButton as HTMLElement).click();
    isWidgetOpen = false;
    console.log('📱 Widget Hector fermé');
  }
};

// Fonction pour détruire le widget
const destroyWidget = () => {
  if (widgetRoot) {
    widgetRoot.unmount();
    widgetRoot = null;
  }
  
  if (widgetContainer) {
    widgetContainer.remove();
    widgetContainer = null;
  }
  
  isWidgetOpen = false;
  console.log('🗑️ Widget Hector détruit');
};

// Fonction pour vérifier si le widget est ouvert
const checkIfOpen = (): boolean => {
  return isWidgetOpen;
};

// API globale
const HectorWidgetAPI: IHectorWidgetAPI = {
  init: initWidget,
  open: openWidget,
  close: closeWidget,
  destroy: destroyWidget,
  isOpen: checkIfOpen,
  version: '1.0.0'
};

// Exposer l'API globalement
if (typeof window !== 'undefined') {
  (window as any).HectorWidget = HectorWidgetAPI;
  
  // Log de bienvenue
  console.log(`
🎉 Widget Hector v${HectorWidgetAPI.version} chargé avec succès !

🚀 UTILISATION ULTRA-SIMPLE:
   HectorWidget.init({
     position: 'bottom',
     buttonColor: '#1a1a1a',
     onSubmit: function(data) {
       console.log('Données reçues:', data);
     }
   });

✨ FONCTIONNALITÉS INTÉGRÉES:
   ✅ Bouton flottant avec design moderne
   ✅ Modal de contact complète
   ✅ Validation de formulaire automatique
   ✅ Autocomplétion d'adresse (API française)
   ✅ Recherche DPE automatique
   ✅ Appel API CallHector intégré
   ✅ Gestion de session automatique
   ✅ Analytics Google intégrés
   ✅ Design responsive mobile/desktop

🔧 Méthodes disponibles:
   HectorWidget.open()    - Ouvrir le widget
   HectorWidget.close()   - Fermer le widget
   HectorWidget.destroy() - Détruire le widget
   HectorWidget.isOpen()  - Vérifier si ouvert
  `);
}

export default HectorWidgetAPI;
